import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IcSearch, IcClose, IcPlus, IcReceiptText } from '../../theme/icons';
import factureService from '../../services/factureService';
import supplierService from '../../services/supplierService';
import authService from '../../services/authService';
import { matchesSearch } from '../../utils/search';

// Accent-insensitive search helper (same as desktop)
// Status meta (label + pill colors) — same logic as desktop ListFacturation
function getStatusMeta(facture) {
  const amountDue = Number(facture.amount_due) || 0;
  const total = Number(facture.total) || 0;
  // Variants = grammaire Étiquettes Ki Para (index.css) — famille paiement.
  if (facture.is_avoir) return { label: 'Avoir', pill: 'bdg-warn' }; // retour fournisseur
  if (amountDue <= 0) {
    return { label: 'Payé', pill: 'bdg-pos' };
  }
  if (amountDue < total) {
    return { label: 'Partiel', pill: 'bdg-alert' };
  }
  return { label: 'Non payé', pill: 'bdg-critical' };
}

function StatusPill({ facture }) {
  const meta = getStatusMeta(facture);
  return (
    <span
      className={`bdg ${meta.pill}`}
    >
      {meta.label}
    </span>
  );
}

function FactureCard({ facture, onOpen }) {
  const total = Number(facture.total) || 0;
  const amountDue = Number(facture.amount_due) || 0;
  const code = facture.number || facture.barrecode || 'N/A';
  const dateStr = facture.date
    ? new Date(facture.date).toLocaleDateString('fr-FR')
    : (facture.date_added ? new Date(facture.date_added).toLocaleDateString('fr-FR') : '');

  // Edit / delete removed here — those actions live in the facture detail
  // (opened by tapping the card). Title = supplier name, subtitle = invoice n°.
  return (
    <div
      onClick={() => onOpen(facture.id)}
      className="flex items-center gap-3 p-3 bg-white rounded-2xl border border-gray-200 cursor-pointer"
    >
      <div className="h-14 w-14 rounded-xl bg-gray-100 text-gray-500 flex-shrink-0 flex items-center justify-center">
        <IcReceiptText className="text-xl" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium text-gray-900 truncate">{facture.supplier_name || 'N/A'}</span>
          <StatusPill facture={facture} />
        </div>
        <div className="text-xs text-gray-500 truncate mt-0.5">
          N° {code}
        </div>
        <div className="mt-1 flex items-center justify-between gap-2">
          <span className="text-xs text-gray-400">{dateStr}</span>
          <span className="text-sm font-semibold text-gray-800">
            {total.toFixed(3)}
            <span className="text-[11px] text-gray-500 ml-0.5">DT</span>
          </span>
        </div>
        {amountDue > 0 && (
          <div className="text-xs text-red-700 font-medium mt-0.5 tabular-nums">
            Reste {amountDue.toFixed(3)} DT
          </div>
        )}
      </div>
    </div>
  );
}

export default function MobileFacturationList() {
  const navigate = useNavigate();
  const [factures, setFactures] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [quickStatus, setQuickStatus] = useState('all'); // 'all' | 'paid' | 'unpaid'
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const canManage = authService.isManagerOrAdmin();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await factureService.getAllFactures();
      setFactures(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading factures:', err);
      setFactures([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSuppliers = useCallback(async () => {
    try {
      const data = await supplierService.getAllSuppliers();
      setSuppliers(Array.isArray(data) ? data : (data.results || []));
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  }, []);

  useEffect(() => {
    load();
    loadSuppliers();
  }, [load, loadSuppliers]);

  const handleOpen = (id) => navigate(`/facturation/detail/${id}`);

  // Client-side filter (search + quick status + supplier) — mirrors desktop logic
  const displayedFactures = factures.filter((f) => {
    const amountDue = Number(f.amount_due) || 0;
    if (quickStatus === 'paid' && amountDue > 0) return false;
    if (quickStatus === 'unpaid' && !(amountDue > 0)) return false;
    if (supplierFilter !== 'all' && String(f.supplier) !== String(supplierFilter)) return false;
    // Règle Ki Para (utils/search.js) : fournisseur par début de mot,
    // n° / code-barres « contient ».
    return matchesSearch(search, f.supplier_name, [f.barrecode, f.number]);
  });

  return (
    <div className="w-full px-4 py-6">
      {/* Nom de page = chip blanc bordure noire, comme MobileProductList
          (Studio 2026-08-14) ; ancien h1 blanc retiré. */}
      <div className="mb-4">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-white text-gray-900">
          <IcReceiptText size={13} /> Facturation
        </span>
      </div>
      {/* Search */}
      <div className="relative mb-3">
        <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher par numéro ou fournisseur..."
          className="w-full pl-11 pr-10 py-2.5 border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <IcClose />
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4">
        <select
          value={quickStatus}
          onChange={(e) => setQuickStatus(e.target.value)}
          className="inp flex-1 min-w-0 rounded-xl px-3 py-2.5 text-sm text-gray-700"
        >
          <option value="all">Tous les statuts</option>
          <option value="paid">Payées</option>
          <option value="unpaid">Non payées</option>
        </select>
        <select
          value={supplierFilter}
          onChange={(e) => setSupplierFilter(e.target.value)}
          className="inp flex-1 min-w-0 rounded-xl px-3 py-2.5 text-sm text-gray-700"
        >
          <option value="all">Tous les fournisseurs</option>
          {suppliers.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      ) : displayedFactures.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <IcReceiptText className="mx-auto text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">Aucune facture trouvée</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {displayedFactures.map((f) => (
            <FactureCard
              key={f.id || f.barrecode}
              facture={f}
              onOpen={handleOpen}
            />
          ))}
        </div>
      )}

      {canManage && (
        <button
          onClick={() => navigate('/facturation/add')}
          aria-label="Ajouter une facture"
          className="btn-p fixed right-4 bottom-24 z-30 h-14 w-14 rounded-full shadow-lg transition-colors"
        >
          <IcPlus className="text-xl" />
        </button>
      )}
    </div>
  );
}
